import { apiClient } from './client'
import type {
  FinancialConnection,
  Account,
  AccountType,
  Transaction,
  PlaidLinkTokenResponse,
  PlaidExchangeRequest,
  PlaidExchangeResponse,
  FlagTransactionRequest,
  SyncFinancialResponse,
  TransactionCategory,
} from '@/types'

export interface TransactionsParams {
  parent_user_id?: string
  start_date?: string
  end_date?: string
  category?: TransactionCategory
  flagged?: boolean
  search?: string
  page?: number
  page_size?: number
}

export interface TransactionsResponse {
  data: Transaction[]
  pagination: {
    page: number
    page_size: number
    total_pages: number
    total_count: number
  }
}

export interface SpendingSummary {
  total: number
  by_category: Partial<Record<TransactionCategory, number>>
  period: string
}

// Paginated response type from Django REST Framework
interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// Backend returns flat account objects - we group them into connections
interface BackendAccount {
  id: string
  connection: string
  parent_user?: string
  parent_user_email?: string
  plaid_account_id: string
  name: string
  official_name?: string
  account_type: string
  account_subtype?: string
  mask: string
  current_balance?: number
  available_balance?: number
  currency: string
  is_monitored: boolean
  last_balance_update?: string
  institution_name: string
  connection_status: string
  connection_connected_at?: string
  connection_last_sync_at?: string | null
  connection_error_message?: string | null
  created_at: string
  updated_at: string
}

export const financialApi = {
  // Plaid Link
  getLinkToken: (familyId: string) =>
    apiClient.post<PlaidLinkTokenResponse>(`/plaid/link/token`, { family_id: familyId }),

  exchangeToken: (data: PlaidExchangeRequest) =>
    apiClient.post<PlaidExchangeResponse>(`/plaid/link/exchange`, data),

  // Financial accounts - backend returns flat accounts, we group them by connection
  getAccounts: async (
    familyId: string,
    params?: { parent_user_id?: string }
  ): Promise<FinancialConnection[]> => {
    const response = await apiClient.get<PaginatedResponse<BackendAccount>>(
      `/families/${familyId}/financial/accounts`,
      { params }
    )

    // Group accounts by connection ID
    const connectionMap = new Map<
      string,
      {
        institution_name: string
        created_at: string
        parent_user?: string
        parent_user_email?: string
        status?: FinancialConnection['status']
        last_sync_at?: string | null
        error_message?: string | null
        accounts: Account[]
      }
    >()

    for (const acc of response.results) {
      if (!connectionMap.has(acc.connection)) {
        connectionMap.set(acc.connection, {
          institution_name: acc.institution_name,
          created_at: acc.connection_connected_at ?? acc.created_at,
          parent_user: acc.parent_user,
          parent_user_email: acc.parent_user_email,
          status: acc.connection_status as FinancialConnection['status'],
          last_sync_at: acc.connection_last_sync_at,
          error_message: acc.connection_error_message,
          accounts: [],
        })
      }

      connectionMap.get(acc.connection)!.accounts.push({
        id: acc.id,
        name: acc.name,
        type: acc.account_type as AccountType,
        mask: acc.mask,
        balance: acc.current_balance,
        currency: acc.currency,
      })
    }

    // Convert map to array of FinancialConnection
    return Array.from(connectionMap.entries()).map(([connectionId, data]) => ({
      id: connectionId,
      institution_name: data.institution_name,
      accounts: data.accounts,
      connected_at: data.created_at,
      parent_user: data.parent_user,
      parent_user_email: data.parent_user_email,
      status: data.status,
      last_sync_at: data.last_sync_at,
      error_message: data.error_message,
    }))
  },

  // Disconnect a Plaid connection
  disconnectConnection: (familyId: string, connectionId: string) =>
    apiClient.delete<void>(`/families/${familyId}/financial/connections/${connectionId}`),

  // Sync all financial data
  syncFinancial: (familyId: string) =>
    apiClient.post<SyncFinancialResponse>(`/families/${familyId}/financial/sync`),

  // Transactions
  getTransactions: async (
    familyId: string,
    params?: TransactionsParams
  ): Promise<TransactionsResponse> => {
    const response = await apiClient.get<PaginatedResponse<Transaction>>(
      `/families/${familyId}/financial/transactions`,
      { params: params as Record<string, string | number | boolean | undefined> }
    )
    return {
      data: response.results,
      pagination: {
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 20,
        total_pages: Math.ceil(response.count / (params?.page_size ?? 20)),
        total_count: response.count,
      },
    }
  },

  getTransaction: (familyId: string, transactionId: string) =>
    apiClient.get<Transaction>(`/families/${familyId}/financial/transactions/${transactionId}`),

  flagTransaction: (familyId: string, transactionId: string, data: FlagTransactionRequest) =>
    apiClient.post<Transaction>(`/families/${familyId}/financial/transactions/${transactionId}/flag`, data),

  unflagTransaction: (familyId: string, transactionId: string) =>
    apiClient.delete<Transaction>(`/families/${familyId}/financial/transactions/${transactionId}/flag`),

  // Spending summary - computed from transactions
  // Note: Backend doesn't have a dedicated spending endpoint, so we compute from transactions
  getSpendingSummary: async (
    familyId: string,
    params?: { period?: '7d' | '30d' | '90d'; parent_user_id?: string }
  ): Promise<SpendingSummary> => {
    const period = params?.period || '30d'
    const days = period === '7d' ? 7 : period === '90d' ? 90 : 30
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    try {
      const response = await apiClient.get<PaginatedResponse<Transaction>>(
        `/families/${familyId}/financial/transactions`,
        {
          params: {
            start_date: startDate.toISOString().split('T')[0],
            parent_user_id: params?.parent_user_id,
            page_size: 500, // Get enough transactions to compute summary
          },
        }
      )

      const transactions = response.results || []
      const total = transactions
        .filter((t) => t.amount < 0) // Spending is negative amounts
        .reduce((sum, t) => sum + Math.abs(t.amount), 0)

      const byCategory: Partial<Record<TransactionCategory, number>> = {}
      transactions
        .filter((t) => t.amount < 0)
        .forEach((t) => {
          const cat = t.category || 'OTHER'
          byCategory[cat] = (byCategory[cat] || 0) + Math.abs(t.amount)
        })

      return { total, by_category: byCategory, period }
    } catch {
      // Return empty summary if transactions fail
      return { total: 0, by_category: {}, period }
    }
  },
}
